import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";

/**
 * Single sidebar item in tree form. The root list contains 0..N items; each
 * may have 0..N children (max nesting depth = 1 by server-side validation).
 */
export interface SidebarItemNode {
  id: string;
  parentId: string | null;
  name: string;
  href: string;
  icon: string | null;
  color: string | null;
  accent: string | null;
  position: number;
  permissionKey: string | null;
  sectionKey: string | null;
  isSystem: boolean;
  isHidden: boolean;
  builtInId: string | null;
  badgeKey: string | null;
  openInNewWindow: boolean;
  children: SidebarItemNode[];
}

export interface SidebarItemsResponse {
  items: SidebarItemNode[];
}

/** Per-page custom CSS overrides for the sidebar. All fields nullable; null = use brand default. */
export interface SidebarStyling {
  activeColor: string | null;
  hoverColor: string | null;
  iconColor: string | null;
}

export interface SidebarStylingResponse {
  styling: SidebarStyling;
}

export const SIDEBAR_ITEMS_QUERY_KEY = ["/api/sidebar-items"] as const;
export const ADMIN_SIDEBAR_ITEMS_QUERY_KEY = ["/api/admin/sidebar-items"] as const;
export const SIDEBAR_STYLING_QUERY_KEY = ["/api/sidebar-styling"] as const;

/** Sidebar tree filtered to the current user's permissions. */
export function useSidebarItems(enabled = true) {
  return useQuery<SidebarItemsResponse>({
    queryKey: SIDEBAR_ITEMS_QUERY_KEY,
    queryFn: () => customFetch<SidebarItemsResponse>("/api/sidebar-items"),
    enabled,
    staleTime: 30_000,
  });
}

/** Full sidebar tree (incl. hidden, no permission filter) — admin builder. */
export function useAdminSidebarItems() {
  return useQuery<SidebarItemsResponse>({
    queryKey: ADMIN_SIDEBAR_ITEMS_QUERY_KEY,
    queryFn: () => customFetch<SidebarItemsResponse>("/api/admin/sidebar-items"),
    staleTime: 0,
  });
}

export interface SidebarItemInput {
  name?: string;
  href?: string;
  icon?: string | null;
  color?: string | null;
  accent?: string | null;
  parentId?: string | null;
  permissionKey?: string | null;
  sectionKey?: string | null;
  isHidden?: boolean;
  badgeKey?: string | null;
  openInNewWindow?: boolean;
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: SIDEBAR_ITEMS_QUERY_KEY });
  qc.invalidateQueries({ queryKey: ADMIN_SIDEBAR_ITEMS_QUERY_KEY });
}

export function useCreateSidebarItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SidebarItemInput) =>
      customFetch<{ item: SidebarItemNode }>("/api/admin/sidebar-items", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSidebarItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SidebarItemInput }) =>
      customFetch<{ item: SidebarItemNode }>(`/api/admin/sidebar-items/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteSidebarItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      customFetch<{ ok: true }>(
        `/api/admin/sidebar-items/${id}${force ? "?force=true" : ""}`,
        { method: "DELETE" },
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

export interface ReorderEntry {
  id: string;
  parentId: string | null;
  position: number;
}

export function useReorderSidebarItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: ReorderEntry[]) =>
      customFetch<SidebarItemsResponse>("/api/admin/sidebar-items/reorder", {
        method: "PUT",
        body: JSON.stringify({ items }),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useResetSidebarItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customFetch<SidebarItemsResponse>("/api/admin/sidebar-items/reset", {
        method: "POST",
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSidebarStyling(enabled = true) {
  return useQuery<SidebarStylingResponse>({
    queryKey: SIDEBAR_STYLING_QUERY_KEY,
    queryFn: () => customFetch<SidebarStylingResponse>("/api/sidebar-styling"),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetSidebarStyling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (styling: SidebarStyling) =>
      customFetch<SidebarStylingResponse>("/api/admin/sidebar-styling", {
        method: "PUT",
        body: JSON.stringify({ styling }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SIDEBAR_STYLING_QUERY_KEY }),
  });
}

/** Flatten a tree into [parent, child, child, parent, ...] in display order. */
export function flattenTree(
  tree: SidebarItemNode[],
): Array<{ node: SidebarItemNode; depth: number }> {
  const out: Array<{ node: SidebarItemNode; depth: number }> = [];
  for (const node of tree) {
    out.push({ node, depth: 0 });
    for (const child of node.children) {
      out.push({ node: child, depth: 1 });
    }
  }
  return out;
}

/** Returns true if the href looks like an external URL we should open in a new tab. */
export function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

// ---------- Sidebar Layouts (named, switchable presets) ----------

export interface SidebarLayout {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  isBuiltIn: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SidebarLayoutsResponse {
  layouts: SidebarLayout[];
}

export const SIDEBAR_LAYOUTS_QUERY_KEY = ["/api/sidebar-layouts"] as const;
export const ADMIN_SIDEBAR_LAYOUTS_QUERY_KEY = ["/api/admin/sidebar-layouts"] as const;

function invalidateLayouts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: SIDEBAR_LAYOUTS_QUERY_KEY });
  qc.invalidateQueries({ queryKey: ADMIN_SIDEBAR_LAYOUTS_QUERY_KEY });
}

export function useAdminSidebarLayouts() {
  return useQuery<SidebarLayoutsResponse>({
    queryKey: ADMIN_SIDEBAR_LAYOUTS_QUERY_KEY,
    queryFn: () =>
      customFetch<SidebarLayoutsResponse>("/api/admin/sidebar-layouts"),
    staleTime: 0,
  });
}

export function useCreateSidebarLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string | null }) =>
      customFetch<{ layout: SidebarLayout }>("/api/admin/sidebar-layouts", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateLayouts(qc),
  });
}

export function useUpdateSidebarLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: { name?: string; description?: string | null };
    }) =>
      customFetch<{ layout: SidebarLayout }>(
        `/api/admin/sidebar-layouts/${id}`,
        { method: "PATCH", body: JSON.stringify(input) },
      ),
    onSuccess: () => invalidateLayouts(qc),
  });
}

export function useDeleteSidebarLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      customFetch<{ ok: true }>(`/api/admin/sidebar-layouts/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateLayouts(qc),
  });
}

/** Activate a layout: replaces live sidebar items + styling with its snapshot. */
export function useActivateSidebarLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      customFetch<SidebarLayoutsResponse>(
        `/api/admin/sidebar-layouts/${id}/activate`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Live sidebar tree + styling were rewritten — invalidate everything.
      qc.invalidateQueries({ queryKey: SIDEBAR_ITEMS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ADMIN_SIDEBAR_ITEMS_QUERY_KEY });
      qc.invalidateQueries({ queryKey: SIDEBAR_STYLING_QUERY_KEY });
      invalidateLayouts(qc);
    },
  });
}

/** Save the current live state into an existing layout's snapshot. */
export function useSaveSidebarLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      customFetch<{ layout: SidebarLayout }>(
        `/api/admin/sidebar-layouts/${id}/save`,
        { method: "POST" },
      ),
    onSuccess: () => invalidateLayouts(qc),
  });
}
